import { useEffect, useRef, useState } from 'react';
import {
    INSPECTION_NOT_RECEIVED_CODE,
    INSPECTION_NOT_RECEIVED_MESSAGE,
    IsInspectable,
    NOT_RECEIVED_STATUSES,
} from '../../constants/pedidoTags';
import {
    INSPECTION_CHECK_STEPS,
    INSPECTION_MAX_PHOTOS_PER_STEP,
    INSPECTION_STEP_KEYS,
    InspectionPhotoMinimum,
    InspectionStepMeta,
} from '../../constants/rmaInspection';
import api from '../../services/api';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import { InspectionChecklist } from './rmaShared';

/**
 * Estação de inspeção do pós-venda — extraída de pages/Rmas.jsx (2026-09-22)
 * para ser reaproveitada pela Gestão de Pedidos sem duplicar código.
 *
 * Contrato (idêntico ao que Rmas.jsx sempre usou):
 *   <InspectionStation
 *       toast                 // useToast() da página (success/error)
 *       onOpenCase(rmaId)     // "abrir caso" — a página decide a navegação
 *       onChanged()           // inspeção registrada — a página recarrega a lista
 *       canLogistics          // rmas:logistics:update
 *       canInspect            // rmas:mediation:update || rmas:logistics:update
 *       fullscreen            // true = takeover (sem moldura própria, input grande)
 *       onInspectingChange(v) // true enquanto o assistente está aberto — a página
 *                             // guarda num ref e NÃO fecha no Esc enquanto v for true
 *   />
 * Takeover de referência: Rmas.jsx (stationOpen + inspectingRef + Esc guardado).
 */

// ── Estação de bipe (2026-08-26, padrão do zpl-checkout) ─────────────────────
// O operador BIPA (etiqueta ET, rastreio, pedido ou nº do caso) e a estação
// decide o passo: RECEBIDO → inspecionar (assistente do checklist, com as
// fotos por etapa + série); INSPECIONADO → já inspecionada (o destino é do
// supervisor, no caso). Input sempre focado, feedback verde/vermelho + beep,
// e volta pro input — loop de WMS. Inspeção registrada: a tela "Inspeção
// concluída" (checklist ✓/⚠) fica sob o input até o próximo bipe.
//
// Chegada ao CD (rodada 7 — decisão do Thiago, 2026-09-23): SÓ entra na
// inspeção a devolução RECEBIDA (chegou ao CD e a Logística etiquetou).
// ABERTO/EM_TRANSITO → aviso com a mensagem do servidor
// (`INSPECTION_NOT_RECEIVED_MESSAGE`, 422 RMA26_NOT_RECEIVED) e bipe grave;
// o assistente não abre.

export function StationBeep(ok) {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = ok ? 880 : 220;
        gain.gain.value = 0.08;
        osc.start();
        osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3));
        osc.onended = () => ctx.close();
    } catch {
        // sem áudio (permissão/navegador) — o feedback visual basta
    }
}

// ── Assistente de inspeção — checklist do dono (2026-09-23) ─────────────────
// Substitui o wizard de 6 passos de 2026-08-26. Uma etapa por tela (padrão
// cockpit, tela cheia): 0 entrada (nº de série + "é o nosso produto?") →
// 1 embalagem de transporte → 2 embalagem do produto → 3 componentes →
// 4 produto → resumo → "Inspeção concluída". Cada etapa 1–4 = resposta +
// fotos DA ETAPA; "Continuar" fica BLOQUEADO até o mínimo de fotos da
// resposta (`InspectionPhotoMinimum`: 1 obrigatória; com problema, 4 — todos
// os lados; produto sem marcas, 0). A inspeção TERMINA NO CHECKLIST (ajuste 2
// do dono): o operador não escolhe destino nem vê orientação — o supervisor
// define o destino depois, no caso.

export const WIZARD_BTN = ' px-8 py-5 text-lg font-bold transition-transform active:scale-95';

const WIZARD_STAGES = ['entrada', ...INSPECTION_STEP_KEYS, 'resumo'];
const STAGE_SHORT_LABELS = {
    entrada: 'Entrada',
    transporte: 'Transporte',
    embalagem: 'Embalagem',
    componentes: 'Componentes',
    produto: 'Produto',
    resumo: 'Resumo',
};
const EMPTY_STEP_PHOTOS = Object.fromEntries(INSPECTION_STEP_KEYS.map((key) => [key, []]));
// Multipart: as fotos de cada etapa vão num campo próprio (`photos_transporte`,
// `photos_embalagem`, `photos_componentes`, `photos_produto`) — o servidor
// grava a etapa no evento ANEXO ("inspeção: embalagem de transporte"…).
const INSPECTION_PHOTO_FIELD_PREFIX = 'photos_';

// Foto de celular (3–8 MB) × até 16+ fotos por inspeção: reduz no navegador
// para no máximo 2048 px (JPEG 0,85) antes de subir. Qualquer falha (HEIC,
// navegador sem createImageBitmap) sobe o original.
const SHRINK_MIN_BYTES = 1.5 * 1024 * 1024;
const SHRINK_MAX_SIDE = 2048;

async function shrinkPhoto(file) {
    if (
        !/^image\/(jpeg|webp)$/i.test(file?.type || '') ||
        file.size < SHRINK_MIN_BYTES ||
        typeof createImageBitmap !== 'function'
    ) {
        return file;
    }
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const scale = Math.min(1, SHRINK_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close?.();
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        if (!blob || blob.size >= file.size) {
            return file;
        }
        const name = `${String(file.name || 'foto').replace(/\.[^.]+$/, '')}.jpg`;
        return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
    } catch {
        return file;
    }
}

/**
 * Fotos de UMA etapa: câmera/arquivo (como antes), miniaturas com remover e
 * o contador `n/mínimo` — o bloqueio do avançar é de quem usa (etapa).
 */
export const StepPhotos = ({ photos, setPhotos, min, max = INSPECTION_MAX_PHOTOS_PER_STEP, hint = null }) => {
    const [processing, setProcessing] = useState(false);
    // Um object URL por foto, criado E revogado no mesmo efeito — a etapa
    // remonta com as fotos já tiradas (voltar, 422 do servidor) e, no
    // StrictMode, o efeito roda duas vezes: URL criada fora dele (useMemo)
    // seria revogada e a miniatura quebraria.
    const [previews, setPreviews] = useState([]);
    useEffect(() => {
        const urls = photos.map((photo) => URL.createObjectURL(photo));
        setPreviews(urls);
        return () => {
            for (const url of urls) {
                URL.revokeObjectURL(url);
            }
        };
    }, [photos]);
    const missing = Math.max(0, min - photos.length);
    const full = photos.length >= max;
    const addFiles = async (picked) => {
        setProcessing(true);
        try {
            const ready = await Promise.all(picked.map((file) => shrinkPhoto(file)));
            setPhotos((prev) => [...prev, ...ready].slice(0, max));
        } finally {
            setProcessing(false);
        }
    };
    return (
        <div className="flex w-full flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                    Fotos da etapa
                </p>
                <p
                    className={`text-sm font-bold tabular-nums ${
                        missing ? 'text-danger-600 dark:text-danger-400' : 'text-emerald-700 dark:text-emerald-400'
                    }`}
                >
                    {min === 0 ? `${photos.length} · opcional` : `${photos.length}/${min} mínimo`}
                </p>
            </div>
            {hint && <p className="text-sm text-mono-600 dark:text-mono-300">{hint}</p>}
            <label
                className={`flex w-full flex-col items-center justify-center gap-1 border-2 border-dashed px-6 py-7 transition-colors ${
                    full
                        ? 'cursor-not-allowed border-mono-200 text-mono-300 dark:border-mono-800 dark:text-mono-600'
                        : 'cursor-pointer border-mono-300 text-mono-500 hover:border-black dark:border-mono-700 dark:hover:border-white'
                }`}
            >
                <span className="text-3xl">📷</span>
                <span className="text-sm font-semibold">
                    {processing
                        ? 'Preparando as fotos…'
                        : full
                          ? `Limite de ${max} fotos nesta etapa`
                          : photos.length
                            ? `${photos.length} foto(s) — toque para adicionar mais`
                            : 'Toque para fotografar'}
                </span>
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={full || processing}
                    onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        e.target.value = '';
                        if (picked.length) {
                            addFiles(picked);
                        }
                    }}
                />
            </label>
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {photos.map((photo, i) => (
                        <button
                            key={`${i}-${photo.name}`}
                            type="button"
                            title="remover"
                            onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                            className="relative h-20 w-20 bg-mono-100 ring-1 ring-mono-300 dark:bg-mono-900 dark:ring-mono-700"
                        >
                            {previews[i] && <img src={previews[i]} alt="" className="h-20 w-20 object-cover" />}
                            <span className="absolute -right-1 -top-1 bg-danger-600 px-1.5 text-[10px] font-bold text-white">
                                ×
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Botões grandes de resposta: a resposta "sem problema" é verde (✓), as de
// problema, vermelhas (⚠); a escolhida fica cheia, as outras em contorno.
const ChoiceButtons = ({ options, value, okValue, onChange }) => (
    <div className="flex flex-wrap justify-center gap-3">
        {options.map((option) => {
            const selected = value === option.value;
            const isOk = option.value === okValue;
            let tone =
                'border-mono-300 bg-white text-mono-700 hover:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-mono-200 dark:hover:border-white';
            if (selected) {
                tone = isOk
                    ? 'border-emerald-600 bg-emerald-600 text-white'
                    : 'border-danger-600 bg-danger-600 text-white';
            }
            return (
                <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onChange(option.value)}
                    className={`${WIZARD_BTN} min-w-[11rem] border-2 uppercase ${tone}`}
                >
                    {isOk ? '✓' : '⚠'} {option.label}
                </button>
            );
        })}
    </div>
);

/**
 * Etapa do erro de validação do servidor (422 RMA26_INSPECTION_PHOTOS_REQUIRED:
 * `details.missing[].step` = etapa sem o mínimo de fotos, a primeira na ordem
 * do assistente) → chave do assistente; sem etapa reconhecível → null.
 */
function errorStep(err) {
    const step = err?.details?.missing?.[0]?.step ?? null;
    return INSPECTION_STEP_KEYS.includes(step) ? step : null;
}

/**
 * Resultado do assistente → multipart do `POST /rmas/:id/inspection` (corpo
 * novo, checklist da 0041): respostas em snake_case e as fotos por etapa.
 */
export function BuildInspectionFormData(result) {
    const formData = new FormData();
    const { checklist } = result;
    formData.append('is_our_product', String(checklist.is_our_product));
    for (const step of INSPECTION_CHECK_STEPS) {
        formData.append(step.field, checklist[step.field]);
    }
    if (checklist.components === 'faltando' && checklist.missing_components) {
        formData.append('missing_components', checklist.missing_components);
    }
    if (result.serialNumber) {
        formData.append('serial_number', result.serialNumber);
    }
    if (result.note) {
        formData.append('note', result.note);
    }
    for (const key of INSPECTION_STEP_KEYS) {
        for (const photo of result.photos[key] || []) {
            formData.append(`${INSPECTION_PHOTO_FIELD_PREFIX}${key}`, photo);
        }
    }
    return formData;
}

/** Fotos por etapa do resultado → contagem (linhas do checklist). */
export function InspectionPhotoCounts(photos) {
    return Object.fromEntries(INSPECTION_STEP_KEYS.map((key) => [key, (photos?.[key] || []).length]));
}

/**
 * Props: `rma` (caso RECEBIDO), `saving`, `onCancel`, `onConfirm(result)` —
 * async; se rejeitar, a mensagem aparece aqui e, se o servidor disser a
 * etapa sem fotos suficientes, o assistente volta para ela. `result` =
 * `{ checklist, serialNumber, note, photos: { transporte: File[], … } }`.
 */
export const InspectionWizard = ({ rma, saving, onCancel, onConfirm }) => {
    const [stage, setStage] = useState('entrada');
    const [error, setError] = useState(null);
    const [serialNumber, setSerialNumber] = useState('');
    const [noSerial, setNoSerial] = useState(false);
    const [answers, setAnswers] = useState({
        is_our_product: null,
        transport_packaging: null,
        product_packaging: null,
        components: null,
        product_condition: null,
    });
    const [missingComponents, setMissingComponents] = useState('');
    const [photos, setPhotos] = useState(EMPTY_STEP_PHOTOS);
    const [note, setNote] = useState('');

    const go = (next) => {
        setError(null);
        setStage(next);
    };
    const stageIndex = WIZARD_STAGES.indexOf(stage);
    const back = () => go(WIZARD_STAGES[Math.max(0, stageIndex - 1)]);
    const forward = () => go(WIZARD_STAGES[Math.min(WIZARD_STAGES.length - 1, stageIndex + 1)]);
    const setAnswer = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));
    const setStepPhotos = (key) => (updater) =>
        setPhotos((prev) => ({ ...prev, [key]: typeof updater === 'function' ? updater(prev[key]) : updater }));

    const checklist = {
        ...answers,
        missing_components: answers.components === 'faltando' ? missingComponents.trim() || null : null,
    };
    const serial = noSerial ? '' : serialNumber.trim();
    const result = { checklist, serialNumber: serial, note: note.trim(), photos };

    const finish = async () => {
        setError(null);
        try {
            await onConfirm(result);
        } catch (err) {
            const step = errorStep(err);
            if (step) {
                setStage(step);
            }
            setError(err?.message || 'Não foi possível registrar a inspeção.');
        }
    };

    // Motivo do bloqueio do "Continuar" da etapa atual (null = liberado).
    let blockReason = null;
    const stepMeta = InspectionStepMeta(stage);
    if (stage === 'entrada') {
        if (!serial && !noSerial) {
            blockReason = 'Informe a série ou marque “não tem série”';
        } else if (answers.is_our_product === null) {
            blockReason = 'Responda: é o nosso produto?';
        }
    } else if (stepMeta) {
        const answer = answers[stepMeta.field];
        const missing = Math.max(0, InspectionPhotoMinimum(stage, answer) - photos[stage].length);
        if (!answer) {
            blockReason = 'Responda acima';
        } else if (missing) {
            blockReason = `Faltam ${missing} foto${missing > 1 ? 's' : ''}`;
        } else if (stage === 'componentes' && answer === 'faltando' && !missingComponents.trim()) {
            blockReason = 'Diga quais componentes faltam';
        }
    }

    const continueButton = (
        <button
            type="button"
            onClick={forward}
            disabled={Boolean(blockReason)}
            title={blockReason || undefined}
            className={`${WIZARD_BTN} bg-black text-white disabled:cursor-not-allowed disabled:bg-mono-200 disabled:text-mono-500 disabled:active:scale-100 dark:bg-white dark:text-black dark:disabled:bg-mono-800 dark:disabled:text-mono-400`}
        >
            {blockReason || 'Continuar →'}
        </button>
    );
    // Durante o envio (16+ fotos pode demorar) não dá para sair nem voltar: o
    // servidor grava a inspeção mesmo assim e o "cancelar" não cancelaria.
    const backLink = (
        <button
            type="button"
            onClick={back}
            disabled={saving}
            className="text-sm font-semibold text-mono-400 underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
        >
            voltar
        </button>
    );

    return (
        <div className="flex flex-col gap-6 border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950 sm:p-8">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                        {stage === 'resumo'
                            ? 'Inspeção · resumo'
                            : `Inspeção · etapa ${stageIndex} de ${INSPECTION_STEP_KEYS.length} — ${STAGE_SHORT_LABELS[stage]}`}
                    </p>
                    <p className="truncate text-sm font-semibold text-black dark:text-white">
                        {rma.item_title || rma.sku}
                        {rma.sku ? ` · ${rma.sku}` : ''}
                    </p>
                </div>
                {saving ? (
                    <span className="shrink-0 text-sm font-semibold text-mono-400" aria-live="polite">
                        Enviando fotos…
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="shrink-0 text-sm font-semibold text-mono-400 underline underline-offset-2 hover:text-danger-600"
                    >
                        cancelar
                    </button>
                )}
            </div>
            {/* Trilho das etapas: feitas em preto, a atual com contorno. */}
            <ol className="grid grid-cols-6 gap-1" aria-label="Etapas da inspeção">
                {WIZARD_STAGES.map((key, i) => (
                    <li key={key} className="flex flex-col gap-1">
                        <span
                            className={`h-1.5 ${
                                i < stageIndex
                                    ? 'bg-black dark:bg-white'
                                    : i === stageIndex
                                      ? 'bg-mono-500 dark:bg-mono-400'
                                      : 'bg-mono-200 dark:bg-mono-800'
                            }`}
                        />
                        <span
                            className={`truncate text-[10px] font-semibold uppercase tracking-wider ${
                                i === stageIndex ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                            }`}
                        >
                            {STAGE_SHORT_LABELS[key]}
                        </span>
                    </li>
                ))}
            </ol>

            {error && (
                <p className="border-l-4 border-l-danger-600 bg-danger-50 px-4 py-3 text-sm font-semibold text-danger-800 dark:bg-danger-400/10 dark:text-danger-200">
                    {error}
                </p>
            )}

            {stage === 'entrada' && (
                <div className="flex flex-col items-center gap-6">
                    <div className="w-full border-2 border-black px-6 py-4 dark:border-white">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                            Produto da venda
                        </p>
                        <p className="break-all font-mono text-3xl font-black text-black dark:text-white">
                            {rma.sku || 'sem SKU'}
                        </p>
                        <p className="text-base text-mono-700 dark:text-mono-200">
                            {rma.item_title || 'Produto sem título'}
                        </p>
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            {rma.external_order_id ? `Pedido ${rma.external_order_id}` : 'Sem pedido'}
                            {Number(rma.quantity) > 1 ? ` · ${rma.quantity} un` : ''}
                        </p>
                    </div>
                    <div className="flex w-full max-w-md flex-col gap-2">
                        <p className="text-center text-[11px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                            Nº de série do produto
                        </p>
                        <input
                            value={noSerial ? '' : serialNumber}
                            onChange={(e) => setSerialNumber(e.target.value)}
                            disabled={noSerial}
                            placeholder={noSerial ? 'não tem série' : 'bipe ou digite a série'}
                            // biome-ignore lint/a11y/noAutofocus: bancada de bipe — o leitor grava direto no campo de série (padrão WMS)
                            autoFocus
                            className="w-full border-2 border-mono-300 bg-white px-5 py-4 text-center font-mono text-xl outline-none focus:border-black disabled:bg-mono-100 disabled:text-mono-400 dark:border-mono-700 dark:bg-mono-900 dark:focus:border-white dark:disabled:bg-mono-900/40"
                        />
                        <label className="flex cursor-pointer items-center justify-center gap-2 text-sm font-semibold text-mono-600 dark:text-mono-300">
                            <input
                                type="checkbox"
                                checked={noSerial}
                                onChange={(e) => setNoSerial(e.target.checked)}
                                className="h-4 w-4 accent-black dark:accent-white"
                            />
                            Não tem série
                        </label>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <p className="text-center text-2xl font-black uppercase tracking-wide text-black dark:text-white">
                            É o nosso produto?
                        </p>
                        <p className="text-center text-sm text-mono-500 dark:text-mono-400">
                            Compare com o SKU e o produto da venda acima.
                        </p>
                        <ChoiceButtons
                            options={[
                                { value: true, label: 'Sim, é o nosso' },
                                { value: false, label: 'Não, é outro' },
                            ]}
                            value={answers.is_our_product}
                            okValue
                            onChange={(value) => setAnswer('is_our_product', value)}
                        />
                    </div>
                    <div className="flex items-center gap-3">{continueButton}</div>
                </div>
            )}

            {stepMeta && (
                <div className="flex flex-col items-center gap-5">
                    <p className="text-center text-2xl font-black uppercase tracking-wide text-black dark:text-white">
                        {stepMeta.question}
                    </p>
                    <ChoiceButtons
                        options={stepMeta.options}
                        value={answers[stepMeta.field]}
                        okValue={stepMeta.ok}
                        onChange={(value) => setAnswer(stepMeta.field, value)}
                    />
                    {stage === 'componentes' && answers.components === 'faltando' && (
                        <Textarea
                            label="Quais componentes faltam?"
                            value={missingComponents}
                            onChange={(e) => setMissingComponents(e.target.value.slice(0, 500))}
                            rows={2}
                            className="w-full max-w-xl"
                        />
                    )}
                    <StepPhotos
                        key={stage}
                        photos={photos[stage]}
                        setPhotos={setStepPhotos(stage)}
                        min={InspectionPhotoMinimum(stage, answers[stepMeta.field])}
                        hint={[
                            stepMeta.photoHint,
                            stepMeta.problem.includes(answers[stepMeta.field]) ? stepMeta.problemHint : null,
                            answers[stepMeta.field] === 'ausente' ? stepMeta.absentHint : null,
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    />
                    <div className="flex items-center gap-3">
                        {backLink}
                        {continueButton}
                    </div>
                </div>
            )}

            {stage === 'resumo' && (
                <div className="flex flex-col items-center gap-5">
                    <p className="text-center text-2xl font-black uppercase tracking-wide text-black dark:text-white">
                        Conferir e finalizar
                    </p>
                    <InspectionChecklist
                        checklist={checklist}
                        serialNumber={serial || null}
                        photoCounts={InspectionPhotoCounts(photos)}
                        size="lg"
                    />
                    <Textarea
                        label="Observações (opcional)"
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, 2000))}
                        rows={2}
                        className="w-full"
                    />
                    <div className="flex items-center gap-3">
                        {backLink}
                        <Button size="lg" loading={saving} onClick={finish}>
                            ✓ Finalizar inspeção
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Tela final "Inspeção concluída": o resumo do checklist em ✓/⚠ — sem
 * destino e sem orientação (quem define o destino é o supervisor). `action`
 * = botão opcional ({ label, onClick }) — a estação não usa (o bipe segue
 * focado logo abaixo); o caso usa "Fechar".
 */
export const InspectionDoneCard = ({ rma, result, action = null }) => (
    <div className="flex flex-col gap-5 border-2 border-emerald-600 bg-white p-4 dark:border-emerald-500 dark:bg-mono-950 sm:p-8">
        <div className="flex flex-col items-center gap-2 text-center">
            <span className="flex h-14 w-14 items-center justify-center bg-emerald-600 text-3xl font-black text-white">
                ✓
            </span>
            <p className="text-2xl font-black uppercase tracking-wide text-black dark:text-white">Inspeção concluída</p>
            <p className="text-sm text-mono-500 dark:text-mono-400">
                {rma?.item_title || rma?.sku || ''}
                {rma?.sku && rma?.item_title ? ` · ${rma.sku}` : ''}
            </p>
        </div>
        <InspectionChecklist
            checklist={result.checklist}
            serialNumber={result.serialNumber || null}
            photoCounts={InspectionPhotoCounts(result.photos)}
            size="lg"
        />
        {action && (
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={action.onClick}
                    className={`${WIZARD_BTN} bg-black text-white dark:bg-white dark:text-black`}
                >
                    {action.label}
                </button>
            </div>
        )}
    </div>
);

/**
 * Inspeção em TELA CHEIA fora da estação (caso /rmas/:id e embutido na
 * Gestão de Pedidos): mesmo takeover da estação, sem Esc — sair é só pelo
 * "cancelar" do assistente ou pelo botão da tela final, para não perder fotos.
 */
export const InspectionScreen = ({ children }) => (
    <div className="fixed inset-0 z-40 flex flex-col bg-mono-50 dark:bg-black">
        <header className="flex items-center border-b border-mono-200 bg-white px-6 py-3 dark:border-mono-800 dark:bg-mono-950">
            <h2 className="text-sm font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                ▮ Inspeção da devolução
            </h2>
        </header>
        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-start overflow-y-auto p-3 pt-6 sm:p-6 sm:pt-10">
            {children}
        </main>
    </div>
);

export const InspectionStation = ({
    toast,
    onOpenCase,
    onChanged,
    // biome-ignore lint/correctness/noUnusedFunctionParameters: faz parte do contrato de props (Rmas.jsx passa) — reservado para ações da logística
    canLogistics,
    canInspect,
    fullscreen = false,
    onInspectingChange = null,
}) => {
    const inputRef = useRef(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null); // { tone, title, hint, rma, label }
    const [inspectTarget, setInspectTarget] = useState(null);
    // Última inspeção registrada: { rma, result } — tela "Inspeção concluída".
    const [done, setDone] = useState(null);
    const [history, setHistory] = useState([]);
    // Avisa a página quando há inspeção em curso — o Esc da tela cheia não
    // pode descartar respostas/fotos sem querer.
    useEffect(() => {
        onInspectingChange?.(Boolean(inspectTarget));
        return () => onInspectingChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inspectTarget]);

    useEffect(() => {
        inputRef.current?.focus();
        // Padrão do zpl-checkout: clique em QUALQUER lugar devolve o foco ao
        // input — o leitor nunca bipa no vazio.
        const clickBack = () => setTimeout(() => inputRef.current?.focus(), 50);
        document.addEventListener('click', clickBack);
        return () => document.removeEventListener('click', clickBack);
    }, []);
    const refocus = () => setTimeout(() => inputRef.current?.focus(), 50);

    const pushHistory = (entry) => setHistory((prev) => [entry, ...prev].slice(0, 8));

    // `beepOk` = bipe agudo (ok); grave para erro — e para aviso que BARRA o
    // passo (caso ainda não recebido).
    const feedback = (tone, title, hint = null, extra = {}, beepOk = tone !== 'error') => {
        StationBeep(beepOk);
        setDone(null);
        setResult({ tone, title, hint, ...extra });
        pushHistory({ tone, title, at: new Date().toLocaleTimeString('pt-BR').slice(0, 5) });
        refocus();
    };

    const scan = async () => {
        const value = code.trim();
        if (!value || busy) {
            return;
        }
        setBusy(true);
        setCode('');
        try {
            const data = await api.resolveRmaScan(value);
            if (!data.rma && data.label) {
                feedback(
                    'warn',
                    `${data.label.code} ainda não associada a uma venda`,
                    'Associe na sub-aba Etiquetas antes de inspecionar.',
                    { label: data.label },
                );
                return;
            }
            if (!data.rma) {
                feedback(
                    'error',
                    `Nada encontrado para "${value}"`,
                    'Bipe a etiqueta ET, o rastreio ou o nº do pedido.',
                );
                return;
            }
            const rma = data.rma;
            const name = rma.item_title || rma.sku || rma.id.slice(-8);
            if (NOT_RECEIVED_STATUSES.includes(rma.status)) {
                // Ainda não recebida (rodada 7): a inspeção só vem depois da
                // chegada ao CD e da etiqueta da Logística.
                feedback(
                    'warn',
                    `Ainda não recebida · ${name}`,
                    INSPECTION_NOT_RECEIVED_MESSAGE,
                    { rma, action: 'abrir' },
                    false,
                );
            } else if (IsInspectable(rma)) {
                // RECEBIDO (etiquetado pela Logística): bipar abre a inspeção.
                StationBeep(true);
                setDone(null);
                setResult({
                    tone: 'ok',
                    title: name,
                    hint: 'Na bancada — inspecionar agora.',
                    rma,
                    action: 'inspecionar',
                });
                pushHistory({ tone: 'ok', title: name, at: new Date().toLocaleTimeString('pt-BR').slice(0, 5) });
                if (canInspect) {
                    setInspectTarget(rma);
                }
            } else if (rma.status === 'INSPECIONADO') {
                feedback('ok', name, 'Já inspecionada — o destino é definido pelo supervisor.', {
                    rma,
                    action: 'abrir',
                });
            } else {
                feedback('warn', name, `Fora do fluxo físico (${rma.status}).`, { rma, action: 'abrir' });
            }
        } catch (err) {
            feedback('error', err.message || 'Erro ao resolver o bipe');
        } finally {
            setBusy(false);
        }
    };

    // Erro que não seja "ainda não recebida" volta ao assistente (ele mostra a
    // mensagem e, se o servidor apontar a etapa sem fotos, volta para ela).
    const confirmInspection = async (inspection) => {
        const target = inspectTarget;
        setBusy(true);
        try {
            await api.registerRmaInspection(target.id, BuildInspectionFormData(inspection));
            StationBeep(true);
            toast.success('Inspeção registrada.');
            const name = target.item_title || target.sku || '';
            setResult(null);
            setDone({ rma: target, result: inspection });
            pushHistory({
                tone: 'ok',
                title: `Inspeção concluída · ${name}`,
                at: new Date().toLocaleTimeString('pt-BR').slice(0, 5),
            });
            setInspectTarget(null);
            refocus();
            onChanged?.();
        } catch (err) {
            if (err?.code === INSPECTION_NOT_RECEIVED_CODE) {
                // O servidor não vê o caso como RECEBIDO: volta ao bipe com o
                // aviso (as respostas do assistente não valem sem recebimento).
                setInspectTarget(null);
                feedback(
                    'warn',
                    `Ainda não recebida · ${target?.item_title || target?.sku || ''}`,
                    err.message || INSPECTION_NOT_RECEIVED_MESSAGE,
                    { rma: target, action: 'abrir' },
                    false,
                );
                return;
            }
            StationBeep(false);
            throw err;
        } finally {
            setBusy(false);
        }
    };

    const tones = {
        ok: 'border-emerald-500/60 bg-emerald-50 text-emerald-900 dark:bg-emerald-400/10 dark:text-emerald-200',
        warn: 'border-warning-600/50 bg-warning-50 text-warning-900 dark:bg-warning-400/10 dark:text-warning-200',
        error: 'border-danger-600/60 bg-danger-50 text-danger-900 dark:bg-danger-400/10 dark:text-danger-200',
    };

    // Inspeção em curso: o assistente etapa-por-etapa TOMA a estação (nada de
    // pop-up) — ao terminar/cancelar, volta pro bipe.
    if (inspectTarget) {
        return (
            <InspectionWizard
                key={inspectTarget.id}
                rma={inspectTarget}
                saving={busy}
                onCancel={() => {
                    setInspectTarget(null);
                    refocus();
                }}
                onConfirm={confirmInspection}
            />
        );
    }

    return (
        <div
            className={
                fullscreen
                    ? 'flex flex-col gap-5'
                    : 'flex flex-col gap-3 border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950'
            }
        >
            <div>
                {!fullscreen && (
                    <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                        Estação de inspeção
                    </p>
                )}
                <p
                    className={
                        fullscreen
                            ? 'text-center text-sm text-mono-500 dark:text-mono-400'
                            : 'text-xs text-mono-500 dark:text-mono-400'
                    }
                >
                    Bipe a etiqueta interna (ET-000123), o rastreio da devolução ou o nº do pedido. Só entra na inspeção
                    a devolução recebida — que chegou ao CD e foi etiquetada pela Logística.
                </p>
            </div>
            <input
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        scan();
                    }
                }}
                placeholder="▮ bipe aqui…"
                autoComplete="off"
                className={`w-full border-2 border-mono-300 bg-white font-mono outline-none transition-colors focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:focus:border-white ${
                    fullscreen ? 'px-6 py-5 text-center text-2xl' : 'px-4 py-3 text-lg'
                }`}
            />
            {done && !result && (
                <div className="flex flex-col gap-2">
                    <InspectionDoneCard rma={done.rma} result={done.result} />
                    <div className="flex justify-center">
                        <Button size="sm" variant="ghost" onClick={() => onOpenCase(done.rma.id)}>
                            abrir caso
                        </Button>
                    </div>
                </div>
            )}
            {result && (
                <div
                    className={`flex flex-wrap items-center justify-between gap-3 border-2 ${fullscreen ? 'px-6 py-5' : 'px-4 py-3'} ${tones[result.tone]}`}
                >
                    <div className="min-w-0">
                        <p className={`truncate font-bold ${fullscreen ? 'text-xl' : 'text-sm'}`}>{result.title}</p>
                        {result.hint && (
                            <p className={`opacity-80 ${fullscreen ? 'text-sm' : 'text-xs'}`}>{result.hint}</p>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        {result.action === 'inspecionar' && canInspect && (
                            <Button size="sm" onClick={() => setInspectTarget(result.rma)}>
                                Inspecionar
                            </Button>
                        )}
                        {result.rma && (
                            <Button size="sm" variant="ghost" onClick={() => onOpenCase(result.rma.id)}>
                                abrir caso
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {history.length > 0 && (
                <div className="flex flex-col gap-0.5">
                    {history.map((entry, i) => (
                        <p key={i} className="truncate text-[11px] text-mono-400 dark:text-mono-600">
                            <span className="font-mono">{entry.at}</span>{' '}
                            <span
                                className={
                                    entry.tone === 'error'
                                        ? 'text-danger-600 dark:text-danger-400'
                                        : entry.tone === 'warn'
                                          ? 'text-warning-text dark:text-warning-300'
                                          : ''
                                }
                            >
                                {entry.title}
                            </span>
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};
